import * as React from 'react';
import { logError } from '../../utils/logger';

function isChunkLoadError(error: Error): boolean {
  return error.name === 'ChunkLoadError' || /loading (css )?chunk/i.test(error.message ?? '');
}

interface ErrorBoundaryFallbackInfo {
  /**
   * Whether the caught error was a stale-bundle ChunkLoadError rather than a genuine crash.
   * Lets the fallback show "an update is available" copy instead of implying something broke.
   */
  isChunkLoadError: boolean;
}

interface ErrorBoundaryProps {
  /**
   * Child content to render as a fallback if there is an error. Either a fixed node, or a
   * function that receives ErrorBoundaryFallbackInfo so the fallback can vary its copy based
   * on why it's being shown (e.g. update-available vs. genuine crash).
   */
  fallback: React.ReactNode | ((info: ErrorBoundaryFallbackInfo) => React.ReactNode);
  /**
   * Child content to render in the happy case when there is no error.
   */
  children: React.ReactNode;
  /**
   * Observer callback to notify if there is an error.
   * Note that this does not override the error boundary's fallback or reporting,
   * but does allow for extra side effects or visibility.
   */
  onError?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkLoadError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isChunkLoadError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, isChunkLoadError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // A stale client (an old browser tab, or a rarely-reopened home-screen-installed PWA)
    // can throw this when it tries to lazy-load a bundle chunk the server no longer has.
    // Tag it distinctly so Sentry can filter/alert on it separately from other crashes —
    // e.g. to flag when the same one recurs even after a user has already tried the
    // restart button below, which points to something more serious than a one-off stale
    // cache. Deliberately not auto-reloading here: this is the shared boundary used by
    // scoped, feature-level consumers too (e.g. the state-program wizard, which keeps
    // users inside it on error rather than navigating away), so forcing a page reload
    // from this class would blow away in-progress state at call sites that were built
    // specifically to avoid that. The root-level boundaries in App.js already show
    // ErrorScreen with a "Restart App" button that reloads on web — that existing,
    // user-initiated path is what actually recovers a stale bundle.
    const details = isChunkLoadError(error) ? { ...info, isChunkLoadError: true } : info;
    logError(error, details);
    // if there is an observer callback registered then call it
    if (this.props.onError) {
      this.props.onError();
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return typeof this.props.fallback === 'function'
        ? this.props.fallback({ isChunkLoadError: this.state.isChunkLoadError })
        : this.props.fallback;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
